import { apiClient } from '@/utils/api'

export type LeagueFormat = 'singles' | 'doubles'
export type LeagueStatus = 'active' | 'archived'
/** Match format: how many sets to win (best of 1, 3, or 5) */
export type MatchFormat = 'bestOf1' | 'bestOf3' | 'bestOf5'

export interface League {
  leagueId: string
  groupId: string
  name: string
  format: LeagueFormat
  /** Match format for scoring (default: bestOf1) */
  matchFormat: MatchFormat
  /** If true, submitted match scores are automatically approved */
  autoApprove: boolean
  description?: string
  status: LeagueStatus
  createdAt: string
  updatedAt: string
}

interface LeaguesResponse {
  data: League[]
  meta: {
    total: number
  }
}

interface LeagueResponse {
  data: League
}

export const leagueService = {
  async list(groupId: string): Promise<League[]> {
    const response = await apiClient.authGet<LeaguesResponse>(`/groups/${groupId}/leagues`)
    return response.data
  },

  /**
   * List leagues for a public group without authentication.
   * Only works for groups with isPublic: true
   */
  async listPublic(groupId: string): Promise<League[]> {
    const response = await apiClient.get<LeaguesResponse>(`/groups/${groupId}/leagues/public`)
    return response.data
  },

  async get(groupId: string, leagueId: string): Promise<League> {
    const response = await apiClient.authGet<LeagueResponse>(`/groups/${groupId}/leagues/${leagueId}`)
    return response.data
  },

  async create(
    groupId: string,
    data: { name: string; format?: LeagueFormat; matchFormat?: MatchFormat; autoApprove?: boolean; description?: string; id?: string }
  ): Promise<League> {
    const response = await apiClient.authPost<LeagueResponse>(`/groups/${groupId}/leagues`, data)
    return response.data
  },

  async update(
    groupId: string,
    leagueId: string,
    data: { name?: string; format?: LeagueFormat; matchFormat?: MatchFormat; autoApprove?: boolean; description?: string; status?: LeagueStatus }
  ): Promise<League> {
    const response = await apiClient.authPut<LeagueResponse>(`/groups/${groupId}/leagues/${leagueId}`, data)
    return response.data
  },

  async delete(groupId: string, leagueId: string): Promise<void> {
    await apiClient.authDelete(`/groups/${groupId}/leagues/${leagueId}`)
  },

  async listMyActiveLeagues(): Promise<UserActiveLeague[]> {
    const response = await apiClient.authGet<{ data: UserActiveLeague[]; meta: { total: number } }>('/me/leagues')
    return response.data
  },
}

/** Active league the user is participating in */
export interface UserActiveLeague {
  leagueId: string
  leagueName: string
  leagueFormat: LeagueFormat
  groupId: string
  groupName: string
  groupIcon?: string
  seasonId: string
  seasonName: string
  bracketId: string
  bracketName: string
}

